/*
Copyright (C) 2017  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import React from "react";
import styled from "styled-components";
import { observer } from "mobx-react";
import { onSwiftRefresh } from "@src/components/modules/TemplateModule/TopBar/refresh";
import { useNavigate } from "react-router";

import MainTemplate from "@src/components/modules/TemplateModule/MainTemplate";
import Navigation from "@src/components/modules/NavigationModule/Navigation";
import FilterList from "@src/components/ui/Lists/FilterList";
import PageHeader from "@src/components/smart/PageHeader";
import AlertModal from "@src/components/ui/AlertModal";
import Modal from "@src/components/ui/Modal";
import TransferExecutionOptions from "@src/components/modules/TransferModule/TransferExecutionOptions";
import DeploymentOptions from "@src/components/modules/TransferModule/DeploymentOptions";
import DeleteTransferModal from "@src/components/modules/TransferModule/DeleteTransferModal";

import type { DropdownAction } from "@src/components/ui/Dropdowns/ActionDropdown";
import type { Field } from "@src/@types/Field";
import type { InstanceScript } from "@src/@types/Instance";

import projectStore from "@src/stores/ProjectStore";
import transferStore from "@src/stores/TransferStore";
import deploymentStore from "@src/stores/DeploymentStore";
import scheduleStore from "@src/stores/ScheduleStore";
import instanceStore from "@src/stores/InstanceStore";
import endpointStore from "@src/stores/EndpointStore";
import notificationStore from "@src/stores/NotificationStore";

import { ThemePalette } from "@src/components/Theme";
import configLoader from "@src/utils/Config";
import { TransferItem } from "@src/@types/MainItem";
import userStore from "@src/stores/UserStore";
import TransferListItem from "@src/components/modules/TransferModule/TransferListItem";
import RequestCutoverDialog from "@src/components/modules/CutoverModule/RequestCutoverDialog";
import minionPoolStore from "@src/stores/MinionPoolStore";
import {
  TRANSFER_COLUMNS,
  transferScenario,
} from "@src/components/modules/TransferModule/TransferListItem/TransferListItem";
import { providerLabel } from "@src/components/modules/EndpointModule/EndpointLogos/ProviderMark";
import {
  ACTIVITY_OPTIONS,
  ClearFiltersButton,
  FilterSelect,
  Segmented,
  ToolbarSearch,
  matchesSearch,
  useListQuery,
  withinWindow,
} from "@src/components/ui/Lists/ListToolbar";
import type { ListQuery } from "@src/components/ui/Lists/ListToolbar";
import { parseApiDate } from "@src/components/ui/Lists/ListTable";
import { ArrowRightLeft } from "lucide-react";

const Wrapper = styled.div<any>``;

const SCHEDULE_POLL_TIMEOUT = 10000;

const getTitle = (item: TransferItem) => item.notes || item.instances[0] || "";

type State = {
  modalIsOpen: boolean;
  selectedTransfers: TransferItem[];
  showCancelExecutionModal: boolean;
  showExecutionOptionsModal: boolean;
  showCreateDeploymentsModal: boolean;
  /** Swift: the transfer a cut-over is being requested for (config `cutoverApprovals`). */
  cutoverTransfer: TransferItem | null;
  showDeleteDisksModal: boolean;
  showDeleteTransfersModal: boolean;
};

type Props = {
  onNavigate: (path: string) => void;
  query: ListQuery;
};

const RUNNING_STATUSES = [
  "RUNNING",
  "PENDING",
  "STARTING",
  "AWAITING_MINION_ALLOCATIONS",
  "CANCELLING",
  "CANCELLING_AFTER_COMPLETION",
];
const FAILED_STATUSES = [
  "ERROR",
  "FAILED_TO_SCHEDULE",
  "FAILED_TO_CANCEL",
  "ERROR_ALLOCATING_MINIONS",
  "DEADLOCKED",
  "STRANDED_AFTER_DEADLOCK",
];
const FILTER_KEYS = ["q", "status", "type", "source", "target", "owner", "activity", "sort"];
const SORT_OPTIONS = [
  { value: "updated", label: "Last updated" },
  { value: "created", label: "Newest first" },
  { value: "name", label: "Name A–Z" },
  { value: "status", label: "Status" },
];

@observer
class TransfersPage extends React.Component<Props, State> {
  state: State = {
    modalIsOpen: false,
    selectedTransfers: [],
    showCancelExecutionModal: false,
    showCreateDeploymentsModal: false,
    cutoverTransfer: null,
    showExecutionOptionsModal: false,
    showDeleteDisksModal: false,
    showDeleteTransfersModal: false,
  };

  pollTimeout = 0;

  stopPolling = false;

  schedulePolling = false;

  schedulePollTimeout = 0;

  paginatedTransferIds: string[] = [];

  offSwiftRefresh?: () => void;

  componentDidMount() {
    // The top bar's Refresh reloads this page's data.
    this.offSwiftRefresh = onSwiftRefresh(() => {
      this.handleReloadButtonClick();
    });
    document.title = "Transfers · SwiftIQ";

    transferStore.resetTransferPagination();

    projectStore.getProjects();
    endpointStore.getEndpoints({ showLoading: true });
    userStore.getAllUsers({
      showLoading: userStore.users.length === 0,
      quietError: true,
    });

    this.stopPolling = false;
    this.pollData();
  }

  componentWillUnmount() {
    this.offSwiftRefresh?.();
    clearTimeout(this.pollTimeout);
    clearTimeout(this.schedulePollTimeout);
    this.stopPolling = true;
  }

  getEndpoint(endpointId: string) {
    return endpointStore.endpoints.find(endpoint => endpoint.id === endpointId);
  }

  statusGroup(item: TransferItem): string[] {
    const status = item.last_execution_status;
    const groups: string[] = [];
    if (RUNNING_STATUSES.includes(status)) groups.push("running");
    if (FAILED_STATUSES.includes(status)) groups.push("failed");
    if (status === "COMPLETED") groups.push("completed");
    if (!status || status === "UNEXECUTED") groups.push("never");
    if (this.isTransferScheduled(item.id)) groups.push("scheduled");
    return groups;
  }

  get filtersActive() {
    return FILTER_KEYS.filter(k => k !== "sort").some(k => this.props.query.get(k));
  }

  clearFilters() {
    const changes: { [k: string]: null } = {};
    FILTER_KEYS.filter(k => k !== "sort").forEach(k => {
      changes[k] = null;
    });
    this.props.query.set(changes);
  }

  endpointName(id: string) {
    return this.getEndpoint(id)?.name;
  }

  userName(id: string) {
    return userStore.users.find(u => u.id === id)?.name;
  }

  /** Every filter but `except`: the option counts show what picking an option would give. */
  filteredTransfers(except?: string): TransferItem[] {
    const q = this.props.query;
    const text = q.get("q");
    const status = q.get("status");
    const type = q.get("type");
    const sources = q.getList("source");
    const targets = q.getList("target");
    const owner = q.get("owner");
    const activity = q.get("activity");
    const items = transferStore.transfers.filter(item => {
      if (except !== "status" && status && !this.statusGroup(item).includes(status)) return false;
      if (except !== "type" && type) {
        const isMigration = transferScenario(item) === "live_migration";
        if ((type === "migration") !== isMigration) return false;
      }
      if (except !== "source" && sources.length && !sources.includes(this.getEndpoint(item.origin_endpoint_id)?.type || "")) return false;
      if (except !== "target" && targets.length && !targets.includes(this.getEndpoint(item.destination_endpoint_id)?.type || "")) return false;
      if (except !== "owner" && owner && item.user_id !== owner) return false;
      if (except !== "activity" && !withinWindow(parseApiDate(item.updated_at || item.created_at), activity)) return false;
      if (except !== "q" && text) {
        return matchesSearch(text, [
          item.id,
          item.notes,
          item.description,
          ...item.instances,
          this.endpointName(item.origin_endpoint_id),
          this.endpointName(item.destination_endpoint_id),
          this.userName(item.user_id),
          item.last_execution_status,
          ...Object.values(item.destination_environment || {}).filter(v => typeof v === "string"),
        ]);
      }
      return true;
    });
    if (except) return items;
    const sort = q.get("sort") || "updated";
    const time = (v?: string) => parseApiDate(v)?.getTime() || 0;
    return items.slice().sort((a, b) => {
      if (sort === "name") return (getTitle(a) || "").localeCompare(getTitle(b) || "");
      if (sort === "created") return time(b.created_at) - time(a.created_at);
      if (sort === "status") return (a.last_execution_status || "").localeCompare(b.last_execution_status || "");
      return time(b.updated_at || b.created_at) - time(a.updated_at || a.created_at);
    });
  }

  renderToolbar() {
    const q = this.props.query;
    const forStatus = this.filteredTransfers("status");
    const count = (group: string) => forStatus.filter(t => this.statusGroup(t).includes(group)).length;
    const providerOptions = (key: "origin_endpoint_id" | "destination_endpoint_id") => {
      const types = new Set<string>();
      transferStore.transfers.forEach(t => {
        const type = this.getEndpoint(t[key])?.type;
        if (type) types.add(type);
      });
      q.getList(key === "origin_endpoint_id" ? "source" : "target").forEach(t => types.add(t));
      return [...types]
        .map(t => ({ value: t, label: providerLabel(t) }))
        .sort((a, b) => a.label.localeCompare(b.label));
    };
    const owners = [...new Set(transferStore.transfers.map(t => t.user_id))]
      .map(id => ({ value: id, label: this.userName(id) || id.slice(0, 8) }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return (
      <>
        <ToolbarSearch
          value={q.get("q")}
          placeholder="Search name, VM, endpoint or owner"
          onChange={v => q.set({ q: v })}
        />
        <Segmented
          label="Status"
          value={q.get("status")}
          onChange={v => q.set({ status: v })}
          options={[
            { value: "", label: "All" },
            { value: "running", label: "Running", count: count("running") },
            { value: "failed", label: "Failed", count: count("failed") },
            { value: "completed", label: "Completed", count: count("completed") },
            { value: "scheduled", label: "Scheduled", count: count("scheduled") },
            { value: "never", label: "Never run", count: count("never") },
          ]}
        />
        <FilterSelect
          label="Type"
          value={q.get("type")}
          onChange={v => q.set({ type: v })}
          options={[
            { value: "replica", label: "Replica" },
            { value: "migration", label: "Migration" },
          ]}
        />
        <FilterSelect
          label="Source"
          multiple
          value={q.getList("source")}
          onChange={v => q.set({ source: v })}
          options={providerOptions("origin_endpoint_id")}
        />
        <FilterSelect
          label="Target"
          multiple
          value={q.getList("target")}
          onChange={v => q.set({ target: v })}
          options={providerOptions("destination_endpoint_id")}
        />
        <FilterSelect
          label="Owner"
          value={q.get("owner")}
          onChange={v => q.set({ owner: v })}
          options={owners}
          allLabel="Anyone"
        />
        <FilterSelect
          label="Activity"
          value={q.get("activity")}
          onChange={v => q.set({ activity: v })}
          options={ACTIVITY_OPTIONS}
          allLabel="Any time"
        />
        <FilterSelect
          label="Sort"
          value={q.get("sort") || "updated"}
          defaultValue="updated"
          onChange={v => q.set({ sort: v === "updated" ? null : v })}
          options={SORT_OPTIONS}
        />
        {this.filtersActive ? <ClearFiltersButton onClick={() => this.clearFilters()} /> : null}
      </>
    );
  }

  rowActions(item: TransferItem): DropdownAction[] {
    const running = RUNNING_STATUSES.includes(item.last_execution_status);
    return [
      { label: "Open", action: () => this.props.onNavigate(`/transfers/${item.id}`) },
      { label: "Executions", action: () => this.props.onNavigate(`/transfers/${item.id}/executions`) },
      { label: "Schedule", action: () => this.props.onNavigate(`/transfers/${item.id}/schedule`) },
      {
        label: "Execute now…",
        disabled: !this.isExecuteEnabled(item),
        action: () => this.setState({ selectedTransfers: [item], showExecutionOptionsModal: true }),
      },
      {
        label: "Cancel execution",
        hidden: !running,
        action: () => this.setState({ selectedTransfers: [item], showCancelExecutionModal: true }),
      },
      this.cutoverApprovals
        ? {
            label: "Request cut-over…",
            action: () => this.openCutover(item),
          }
        : {
            label: "Deploy…",
            action: () => this.setState({ selectedTransfers: [item] }, () => this.handleShowCreateDeploymentsModal()),
          },
      {
        label: "Delete…",
        color: ThemePalette.alert,
        action: () => this.setState({ selectedTransfers: [item] }, () => this.handleShowDeleteTransfers()),
      },
    ];
  }

  get cutoverApprovals() {
    return configLoader.config?.cutoverApprovals !== false;
  }

  openCutover(item: TransferItem) {
    instanceStore.loadInstancesDetails({
      endpointId: item.origin_endpoint_id,
      instances: item.instances.map(id => ({ id })),
      env: item.source_environment,
      quietError: true,
    });
    minionPoolStore.loadMinionPools();
    this.setState({ cutoverTransfer: item, modalIsOpen: true });
  }

  closeCutover() {
    this.setState({ cutoverTransfer: null, modalIsOpen: false }, () => {
      this.pollData();
    });
  }

  getStatus(transfer?: TransferItem | null): string {
    return transfer?.last_execution_status || "";
  }

  handleProjectChange() {
    transferStore.resetTransferPagination();
    transferStore.getTransfers();
    endpointStore.getEndpoints({ showLoading: true });
  }

  handleReloadButtonClick() {
    projectStore.getProjects();
    transferStore.getTransfers({ showLoading: true });
    endpointStore.getEndpoints({ showLoading: true });
    userStore.getAllUsers({ showLoading: true, quietError: true });
  }

  handleItemClick(item: TransferItem) {
    if (item.last_execution_status === "RUNNING") {
      this.props.onNavigate(`/transfers/${item.id}/executions`);
    } else {
      this.props.onNavigate(`/transfers/${item.id}`);
    }
  }

  handlePaginatedItemsChange(paginatedTransfers: TransferItem[]) {
    this.paginatedTransferIds = paginatedTransfers.map(r => r.id);
  }

  executeSelectedTransfers(fields: Field[]) {
    this.state.selectedTransfers.forEach(transfer => {
      const actualTransfer = transferStore.transfers.find(
        r => r.id === transfer.id,
      );
      if (actualTransfer && this.isExecuteEnabled(actualTransfer)) {
        transferStore.execute(transfer.id, fields);
      }
    });
    notificationStore.alert("Executing selected transfers");
    this.setState({ showExecutionOptionsModal: false });
  }

  deploySelectedTransfers(fields: Field[], uploadedScripts: InstanceScript[]) {
    notificationStore.alert("Creating deployments from selected transfers");
    this.deploy(fields, uploadedScripts);
    this.setState({ showCreateDeploymentsModal: false, modalIsOpen: false });
  }

  async deploy(fields: Field[], uploadedScripts: InstanceScript[]) {
    await Promise.all(
      this.state.selectedTransfers.map(transfer =>
        deploymentStore.deployTransfer({
          transferId: transfer.id,
          fields,
          uploadedUserScripts: uploadedScripts.filter(
            s =>
              !s.instanceId || transfer.instances.find(i => i === s.instanceId),
          ),
          removedUserScripts: [],
          userScriptData: transfer.user_scripts,
          minionPoolMappings:
            transfer.instance_osmorphing_minion_pool_mappings || {},
        }),
      ),
    );
    notificationStore.alert(
      "Deployments successfully created from transfers.",
      "success",
    );
    this.props.onNavigate("/deployments");
  }

  handleShowDeleteTransfers() {
    transferStore.loadHaveTransfersDisks(this.state.selectedTransfers);
    this.setState({ showDeleteTransfersModal: true });
  }

  deleteTransfersDisks(transfers: TransferItem[]) {
    transfers.forEach(transfer => {
      transferStore.deleteDisks(transfer.id);
    });
    this.setState({
      showDeleteDisksModal: false,
      showDeleteTransfersModal: false,
    });
    notificationStore.alert("Deleting selected transfers' disks");
  }

  cancelExecutions() {
    this.state.selectedTransfers.forEach(transfer => {
      const actualTransfer = transferStore.transfers.find(
        r => r.id === transfer.id,
      );
      if (
        actualTransfer?.last_execution_status === "RUNNING" ||
        actualTransfer?.last_execution_status === "AWAITING_MINION_ALLOCATIONS"
      ) {
        transferStore.cancelExecution({ transferId: transfer.id });
      }
    });
    this.setState({ showCancelExecutionModal: false });
  }

  isExecuteEnabled(transfer?: TransferItem | null): boolean {
    if (!transfer) {
      return false;
    }
    const usableTransfer = transfer;
    const originEndpoint = endpointStore.endpoints.find(
      e => e.id === usableTransfer.origin_endpoint_id,
    );
    const targetEndpoint = endpointStore.endpoints.find(
      e => e.id === usableTransfer.destination_endpoint_id,
    );
    const status = this.getStatus(usableTransfer);
    return Boolean(
      originEndpoint &&
        targetEndpoint &&
        status !== "RUNNING" &&
        status !== "AWAITING_MINION_ALLOCATIONS",
    );
  }

  deleteSelectedTransfers() {
    this.state.selectedTransfers.forEach(transfer => {
      transferStore.delete(transfer.id);
    });
    this.setState({ showDeleteTransfersModal: false });
  }

  handleEmptyListButtonClick() {
    this.props.onNavigate("/wizard/migration");
  }

  handleModalOpen() {
    this.setState({ modalIsOpen: true });
  }

  handleModalClose() {
    this.setState({ modalIsOpen: false }, () => {
      this.pollData();
    });
  }

  handleShowCreateDeploymentsModal() {
    instanceStore.loadInstancesDetailsBulk(
      transferStore.transfers.map(r => ({
        endpointId: r.origin_endpoint_id,
        instanceIds: r.instances,
        env: r.source_environment,
      })),
    );

    this.setState({ showCreateDeploymentsModal: true, modalIsOpen: true });
  }

  async pollData() {
    if (this.state.modalIsOpen || this.stopPolling) {
      return;
    }

    await Promise.all([
      transferStore.getTransfers({ skipLog: true }),
      endpointStore.getEndpoints({ skipLog: true }),
      userStore.getAllUsers({ skipLog: true, quietError: true }),
    ]);
    if (!this.schedulePolling) {
      this.pollSchedule();
    }
    this.pollTimeout = window.setTimeout(() => {
      this.pollData();
    }, configLoader.config.requestPollTimeout);
  }

  async pollSchedule() {
    if (
      this.state.modalIsOpen ||
      this.stopPolling ||
      transferStore.transfers.length === 0
    ) {
      return;
    }
    this.schedulePolling = true;
    await scheduleStore.getSchedulesBulk(
      this.paginatedTransferIds.length
        ? this.paginatedTransferIds
        : transferStore.transfers.map(t => t.id),
    );
    this.schedulePollTimeout = window.setTimeout(() => {
      this.pollSchedule();
    }, SCHEDULE_POLL_TIMEOUT);
  }

  isTransferScheduled(transferId: string): boolean {
    const bulkScheduleItem = scheduleStore.bulkSchedules.find(
      b => b.transferId === transferId,
    );
    if (!bulkScheduleItem) {
      return false;
    }
    const result = Boolean(bulkScheduleItem.schedules.find(s => s.enabled));
    return result;
  }

  render() {
    let atLeastOneHasExecuteEnabled = false;
    let atLeaseOneIsRunning = false;
    this.state.selectedTransfers.forEach(transfer => {
      const storeTransfer = transferStore.transfers.find(
        r => r.id === transfer.id,
      );
      atLeastOneHasExecuteEnabled =
        atLeastOneHasExecuteEnabled || this.isExecuteEnabled(storeTransfer);
      const status = this.getStatus(storeTransfer);
      atLeaseOneIsRunning =
        atLeaseOneIsRunning ||
        status === "RUNNING" ||
        status === "AWAITING_MINION_ALLOCATIONS";
    });

    const transfersWithDisabledExecutionOptions =
      this.state.selectedTransfers.filter(transfer =>
        configLoader.config.providersDisabledExecuteOptions.find(
          p =>
            p ===
            endpointStore.endpoints.find(
              e => e.id === transfer.origin_endpoint_id,
            )?.type,
        ),
      );

    const BulkActions: DropdownAction[] = [
      {
        label: "Execute",
        action: () => {
          this.setState({ showExecutionOptionsModal: true });
        },
        disabled: !atLeastOneHasExecuteEnabled,
      },
      {
        label: "Cancel",
        disabled: !atLeaseOneIsRunning,
        action: () => {
          this.setState({ showCancelExecutionModal: true });
        },
      },
      this.cutoverApprovals
        ? {
            label: "Request cut-over",
            color: ThemePalette.primary,
            disabled: this.state.selectedTransfers.length !== 1,
            title:
              this.state.selectedTransfers.length !== 1
                ? "Cut-overs are requested one transfer at a time: select one transfer"
                : null,
            action: () => {
              this.openCutover(this.state.selectedTransfers[0]);
            },
          }
        : {
            label: "Deploy",
            color: ThemePalette.primary,
            action: () => {
              this.handleShowCreateDeploymentsModal();
            },
          },
      {
        label: "Delete disks",
        action: () => {
          this.setState({ showDeleteDisksModal: true });
        },
      },
      {
        label: "Delete",
        color: ThemePalette.alert,
        action: () => {
          this.handleShowDeleteTransfers();
        },
      },
    ];

    return (
      <Wrapper>
        <MainTemplate
          navigationComponent={<Navigation currentPage="transfers" />}
          listComponent={
            <FilterList
              toolbar={this.renderToolbar()}
              filtersActive={this.filtersActive}
              onClearFilters={() => this.clearFilters()}
              columns={TRANSFER_COLUMNS}
              selectionLabel="transfer"
              loading={transferStore.loading}
              items={this.filteredTransfers()}
              dropdownActions={BulkActions}
              onItemClick={item => {
                this.handleItemClick(item);
              }}
              onReloadButtonClick={() => {
                this.handleReloadButtonClick();
              }}
              onSelectedItemsChange={selectedTransfers => {
                this.setState({ selectedTransfers: selectedTransfers });
              }}
              onPaginatedItemsChange={paginatedTransfers => {
                this.handlePaginatedItemsChange(paginatedTransfers);
              }}
              apiPagination={{
                currentPage: transferStore.transfersPage,
                hasNextPage: transferStore.transfersHasNextPage,
                itemsPerPage: transferStore.transfersItemsPerPage,
                onPageChange: page => {
                  transferStore.setTransfersPage(page);
                },
                onItemsPerPageChange: e => {
                  transferStore.setTransfersItemsPerPage(
                    parseInt(e.target.value, 10),
                  );
                },
              }}
              renderItemComponent={options => (
                <TransferListItem
                  {...options}
                  showScheduleIcon={this.isTransferScheduled(options.item.id)}
                  endpointType={id => {
                    const endpoint = this.getEndpoint(id);
                    if (endpoint) {
                      return endpoint.type;
                    }
                    if (endpointStore.loading) {
                      return "Loading...";
                    }
                    return "Not Found";
                  }}
                  getEndpointName={id => this.endpointName(id)}
                  getUserName={id =>
                    userStore.users.find(u => u.id === id)?.name
                  }
                  userNameLoading={userStore.allUsersLoading}
                  actions={this.rowActions(options.item)}
                />
              )}
              emptyListIcon={ArrowRightLeft}
              emptyListMessage="No transfers in this project yet"
              emptyListExtraMessage="A transfer copies virtual machines from a source cloud to a target, incrementally, until you deploy them."
              emptyListButtonLabel="New transfer"
              onEmptyListButtonClick={() => {
                this.handleEmptyListButtonClick();
              }}
            />
          }
          headerComponent={
            <PageHeader
              title="Transfers"
              subtitle="Replicate and migrate workloads between clouds."
              onProjectChange={() => {
                this.handleProjectChange();
              }}
              onModalOpen={() => {
                this.handleModalOpen();
              }}
              onModalClose={() => {
                this.handleModalClose();
              }}
            />
          }
        />
        {this.state.showDeleteTransfersModal ? (
          <DeleteTransferModal
            isMultiTransferSelection
            hasDisks={transferStore.transfersWithDisks.length > 0}
            loading={transferStore.transfersWithDisksLoading}
            onRequestClose={() => {
              this.setState({ showDeleteTransfersModal: false });
            }}
            onDeleteTransfer={() => {
              this.deleteSelectedTransfers();
            }}
            onDeleteDisks={() => {
              this.deleteTransfersDisks(transferStore.transfersWithDisks);
            }}
          />
        ) : null}
        {this.state.showCancelExecutionModal ? (
          <AlertModal
            isOpen
            title="Cancel executions?"
            message="Are you sure you want to cancel the selected transfers' executions?"
            extraMessage=" "
            onConfirmation={() => {
              this.cancelExecutions();
            }}
            onRequestClose={() => {
              this.setState({ showCancelExecutionModal: false });
            }}
          />
        ) : null}
        {this.state.showExecutionOptionsModal ? (
          <Modal
            isOpen
            title="New executions for selected transfers"
            onRequestClose={() => {
              this.setState({ showExecutionOptionsModal: false });
            }}
          >
            <TransferExecutionOptions
              disableExecutionOptions={
                transfersWithDisabledExecutionOptions.length ===
                this.state.selectedTransfers.length
              }
              onCancelClick={() => {
                this.setState({ showExecutionOptionsModal: false });
              }}
              onExecuteClick={fields => {
                this.executeSelectedTransfers(fields);
              }}
            />
          </Modal>
        ) : null}
        {this.state.showCreateDeploymentsModal ? (
          <Modal
            isOpen
            title="Deploy selected transfers"
            onRequestClose={() => {
              this.setState({
                showCreateDeploymentsModal: false,
                modalIsOpen: false,
              });
            }}
          >
            <DeploymentOptions
              transferItem={null}
              minionPools={[]}
              instances={instanceStore.instancesDetails}
              loadingInstances={instanceStore.loadingInstancesDetails}
              onCancelClick={() => {
                this.setState({
                  showCreateDeploymentsModal: false,
                  modalIsOpen: false,
                });
              }}
              onDeployClick={options => {
                this.deploySelectedTransfers(
                  options.fields,
                  options.uploadedUserScripts,
                );
              }}
            />
          </Modal>
        ) : null}
        {this.state.cutoverTransfer ? (
          <RequestCutoverDialog
            transfer={{ executions: [], ...(this.state.cutoverTransfer as any) }}
            instancesDetails={instanceStore.instancesDetails}
            loadingInstances={instanceStore.loadingInstancesDetails}
            minionPools={minionPoolStore.minionPools.filter(
              m =>
                m.endpoint_id === this.state.cutoverTransfer?.destination_endpoint_id &&
                m.platform === "destination",
            )}
            onClose={() => this.closeCutover()}
            onCreated={request => this.props.onNavigate(`/cutovers/${request.id}`)}
          />
        ) : null}
        {this.state.showDeleteDisksModal ? (
          <AlertModal
            isOpen
            title="Delete selected transfers' disks?"
            message="Are you sure you want to delete the selected transfers' disks?"
            extraMessage="Deleting SwiftIQ transfer disks is permanent!"
            onConfirmation={() => {
              this.deleteTransfersDisks(this.state.selectedTransfers);
            }}
            onRequestClose={() => {
              this.setState({ showDeleteDisksModal: false });
            }}
          />
        ) : null}
      </Wrapper>
    );
  }
}

function TransfersPageWithNavigate() {
  const navigate = useNavigate();
  const query = useListQuery();

  return <TransfersPage onNavigate={navigate} query={query} />;
}

export default TransfersPageWithNavigate;
